import { useEffect, useRef, useState } from "react";
import { hostRoom, joinRoom, VERSUS_HOST_COST, type Session } from "../../gamzone/net/peer.ts";
import { getPoints, spendPoints } from "../../gamzone/points.ts";
import { t, useT } from "../../gamzone/i18n.ts";

/**
 * 1:1 lobby (ported from Coin-puzzle's ui/Lobby.jsx): host a room (get a
 * 4-digit code) or join one. On connect it runs a hello/ack handshake to
 * confirm the data channel, then hands the Session to onConnected. Per-game
 * versus state sync (see net.ts) is layered on top.
 */
export default function Lobby({
  onConnected,
  onCancel,
  points = 0,
}: {
  onConnected: (session: Session) => void;
  onCancel: () => void;
  points?: number;
}) {
  const [mode, setMode] = useState<"host" | "join" | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const sessionRef = useRef<Session | null>(null);
  const tt = useT();

  useEffect(() => () => sessionRef.current?.close(), []);

  const handleHost = async () => {
    // Versus costs points — the HOST only is charged. Block up front if the
    // wallet can't cover it, so no room is even created.
    if (getPoints() < VERSUS_HOST_COST) {
      setError(tt("notEnoughPoints", { points: VERSUS_HOST_COST }));
      return;
    }
    setMode("host");
    setError("");
    setStatus(tt("connecting"));
    try {
      const session = await hostRoom({
        onRoomReady: (code) => {
          setRoomCode(code);
          setStatus(tt("waitingForGuest"));
        },
        onError: (e) => setError(String((e as { message?: string })?.message ?? e)),
      });
      // Guest connected → the match is starting. Charge the host now
      // (re-check at spend time in case the balance changed while waiting).
      if (!spendPoints(VERSUS_HOST_COST)) {
        session.close();
        setError(tt("notEnoughPoints", { points: VERSUS_HOST_COST }));
        setStatus("");
        return;
      }
      sessionRef.current = session;
      runHelloHandshake(session, setStatus);
      onConnected(session);
      sessionRef.current = null;
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e));
      setStatus(tt("connectFailed"));
    }
  };

  const handleJoin = async () => {
    setError("");
    const code = inputCode.trim();
    if (!code) {
      setError(tt("enterRoomCode"));
      return;
    }
    setMode("join");
    setStatus(`${tt("connecting")} [${code}]`);
    try {
      const session = await joinRoom(code, {
        onError: (e) => setError(String((e as { message?: string })?.message ?? e)),
      });
      sessionRef.current = session;
      runHelloHandshake(session, setStatus);
      onConnected(session);
      sessionRef.current = null;
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e));
      setStatus(tt("connectFailed"));
    }
  };

  return (
    <div className="lobby">
      <h2 className="lobby-title">{tt("versus", { points: VERSUS_HOST_COST })}</h2>
      <p className="lobby-sub">{tt("lobbySub")}</p>

      {mode === null && (
        <div className="lobby-actions">
          {/* Same treatment as the reference: host cost gated up front with a
              disabled + tooltip; joining (guest) is free so it's never blocked. */}
          <button
            className="lobby-btn host"
            onClick={handleHost}
            disabled={points < VERSUS_HOST_COST}
            title={
              points < VERSUS_HOST_COST
                ? tt("notEnoughPoints", { points: VERSUS_HOST_COST })
                : undefined
            }
          >
            {tt("hostGame")}
          </button>
          <div className="lobby-divider">{tt("or")}</div>
          <input
            className="lobby-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={tt("enterRoomCode")}
            maxLength={4}
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleJoin();
              }
            }}
          />
          <button className="lobby-btn join" onClick={handleJoin}>
            {tt("joinGame")}
          </button>
        </div>
      )}

      {mode === "host" && roomCode && (
        <div className="lobby-room">
          <div className="lobby-room-label">{tt("roomCode")}</div>
          <div className="lobby-room-code">{roomCode}</div>
        </div>
      )}

      {status && <div className="lobby-status">{status}</div>}
      {error && <div className="lobby-error">⚠ {error}</div>}

      <button
        className="lobby-cancel"
        onClick={() => {
          sessionRef.current?.close();
          onCancel();
        }}
      >
        {tt("cancel")}
      </button>
    </div>
  );
}

// Host → hello, guest → hello-ack, host surfaces RTT.
function runHelloHandshake(session: Session, setStatus: (s: string) => void) {
  const t0 = performance.now();
  session.onMessage((raw) => {
    const msg = raw as { type?: string; t?: number } | null;
    if (msg?.type === "hello" && session.role === "guest") {
      session.send({ type: "hello-ack", t: msg.t });
    } else if (msg?.type === "hello-ack" && session.role === "host") {
      const rtt = Math.round(performance.now() - (msg.t ?? t0));
      setStatus(`${t("connected")} · RTT ${rtt}ms`);
    }
  });
  if (session.role === "host") session.send({ type: "hello", t: t0 });
  else setStatus(t("waitingHostResponse"));
}
